import React from 'react';
import { Box, Typography, Button, Divider, Stack } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import UndoIcon from '@mui/icons-material/Undo';
import { useSelector, useDispatch } from 'react-redux';

import { selectCartItems, selectCartTotals, clearCart } from '../store/cartSlice';
import CartItem from './CartItem';

/**
 * Cart panel showing line items and totals.
 * Reads from the Redux store.
 */
export default function Cart({ onCheckout, onReturns }) {
  const dispatch = useDispatch();
  const items = useSelector(selectCartItems);
  const { subtotal, itemCount } = useSelector(selectCartTotals);

  if (items.length === 0) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          gap: 1,
        }}
      >
        <ShoppingCartIcon sx={{ fontSize: 48, opacity: 0.3 }} />
        <Typography variant="body2">Cart is empty</Typography>
        <Typography variant="caption">Click items to add them</Typography>
        {onReturns && (
          <Button
            size="small"
            startIcon={<UndoIcon />}
            onClick={onReturns}
            sx={{ mt: 2 }}
          >
            Process Return
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {itemCount} item{itemCount !== 1 ? 's' : ''}
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={() => dispatch(clearCart())}
        >
          Clear
        </Button>
      </Stack>

      <Divider />

      {/* Scrollable item list */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {items.map((item) => (
          <CartItem key={item.item_code} item={item} />
        ))}
      </Box>

      {/* Totals + Checkout */}
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6">Subtotal</Typography>
          <Typography variant="h6" fontWeight={700}>
            {subtotal.toFixed(2)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {onReturns && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<UndoIcon />}
              onClick={onReturns}
              sx={{ flexShrink: 0 }}
            >
              Return
            </Button>
          )}
          {onCheckout && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={onCheckout}
            >
              Checkout (F2)
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
